import { useMemo } from "react";

import type { ICaaOrder } from "../types/api/customer-account";
import { CountryCode } from "../types/generated/storefront.types";
import { getTrackingInfoCaa } from "../utils/order";

/**
 * classic useOrderInfo의 CAA 대응
 *
 * classic과 다른 점
 * - orderInfo(admin 조회 fallback) 인자가 없음 (CAA는 order(id:) 단건 조회가 됨)
 * - subTotalAmount: CAA order.subtotal은 환불/취소되면 그만큼 줄어(전체 환불이면 0) 주문 당시 금액을 알 수 없음
 *   → 환불 후에도 유지되는 라인의 할인 전 단가(price) × 주문 수량(quantity)에서 라인에 배분된 할인을 빼서 구함
 * - totalAmount: CAA order.totalPrice와 totalTax/totalDuties는 환불되면 줄어들어 합산으로는 주문 당시 결제 금액을 알 수 없음
 *   → 환불 전 결제된 총액인 paymentInformation.totalPaidAmount를 사용
 * - CAA Order엔 세금 포함 여부(taxesIncluded)가 없어 스토어가 옵션으로 넘김
 * - dutyAmount: classic은 totalPrice에서 역산하지만 CAA order.totalPrice는 반품된 라인 금액을 뺀 값이라 totalDuties를 그대로 사용
 * - shippingTitle: CAA는 Order.customAttributes(shippingTitle)를 조회할 수 없어 송장의 배송사명으로 대신하고, 없으면 빈 문자열
 *   TODO: 주문 메타필드로 복사되면 classic과 동일하게 교체
 */
export default function useOrderInfoCaa(order?: ICaaOrder | null, { taxesIncluded = false }: { taxesIncluded?: boolean } = {}) {
  // 총 상품 갯수
  const totalQuantity = useMemo(() => order?.lineItems.edges.reduce((a, b) => a + b.node.quantity, 0) || 0, [order]);

  // 배송비
  const shippingAmount = useMemo(() => parseFloat(order?.totalShipping?.amount || "0"), [order]);

  // 라인에 배분된 할인 — discountApplication.__typename은 classic Storefront API와 같아 classic과 같은 기준으로 분류한다
  const discountAllocations = useMemo(() => order?.lineItems.edges.flatMap(({ node }) => node.discountAllocations) ?? [], [order]);

  // 주문 원래 금액 — 관부가세, 배송비를 제외한 상품 가격의 합 (할인 적용 후)
  const subTotalAmount = useMemo(() => {
    if (!order) return 0;

    const hasAllPrices = order.lineItems.edges.every(({ node }) => node.price);

    // 단가가 하나라도 없는 경우 order.subtotal로 노출되도록 함
    if (!hasAllPrices) return parseFloat(order.subtotal?.amount || "0");

    const lineItemAmount = order.lineItems.edges.reduce((sum, { node }) => sum + node.quantity * parseFloat(node.price?.amount || "0"), 0);
    const discountAmount = discountAllocations.reduce((sum, item) => sum + parseFloat(item.allocatedAmount.amount || "0"), 0);

    return lineItemAmount - discountAmount;
  }, [order, discountAllocations]);

  // 환불된 금액
  const refundAmount = useMemo(() => parseFloat(order?.totalRefunded?.amount || "0"), [order]);

  // 상시 할인 금액
  const autoDiscountAmount = useMemo(
    () =>
      discountAllocations
        .filter(
          ({ discountApplication }) =>
            discountApplication.__typename === "AutomaticDiscountApplication" ||
            discountApplication.__typename === "ManualDiscountApplication",
        )
        .reduce((prev, curr) => prev + parseFloat(curr.allocatedAmount.amount || "0"), 0),
    [discountAllocations],
  );

  // 상시 할인 이름
  const autoDiscountTitle = useMemo(() => {
    const titles = new Set<string>();

    discountAllocations.forEach(({ discountApplication }) => {
      if (
        discountApplication.__typename === "AutomaticDiscountApplication" ||
        discountApplication.__typename === "ManualDiscountApplication"
      ) {
        titles.add(discountApplication.title);
      }
    });

    if (titles.has("Employee")) {
      return "Employee";
    }

    if (titles.has("Friends")) {
      return "Friends";
    }

    return Array.from(titles).join(", ");
  }, [discountAllocations]);

  // 할인 코드 금액
  const codeDiscountAmount = useMemo(
    () =>
      discountAllocations
        .filter(({ discountApplication }) => discountApplication.__typename === "DiscountCodeApplication")
        .reduce((prev, curr) => prev + parseFloat(curr.allocatedAmount.amount || "0"), 0),
    [discountAllocations],
  );

  // 할인 코드 이름 — 라인에 배분되지 않은 코드(배송비 무료 등)도 포함되도록 주문 단위 discountApplications도 함께 본다
  const codeDiscountTitle = useMemo(() => {
    const titles = new Set<string>();

    order?.discountApplications?.edges.forEach(({ node }) => {
      if (node.__typename === "DiscountCodeApplication") {
        titles.add(node.code);
      }
    });

    discountAllocations.forEach(({ discountApplication }) => {
      if (discountApplication.__typename === "DiscountCodeApplication") {
        titles.add(discountApplication.code);
      }
    });

    return Array.from(titles).join(", ");
  }, [order, discountAllocations]);

  // 부가세 금액
  const taxAmount = useMemo(() => parseFloat(order?.totalTax?.amount || "0"), [order]);

  // 관세 금액
  const dutyAmount = useMemo(() => parseFloat(order?.totalDuties?.amount || "0"), [order]);

  // 주문 총 금액 — 실제 결제된 금액 (상품 + 관부가세 + 배송비)
  const totalAmount = useMemo(() => {
    const paidAmount = parseFloat(order?.paymentInformation?.totalPaidAmount.amount || "0");

    // 아직 결제 전(결제 대기)인 경우 합산 금액으로 노출되도록 함 — 세금 포함가면 subtotal에 세금이 이미 들어 있어 더하지 않는다
    return paidAmount || subTotalAmount + (taxesIncluded ? 0 : taxAmount) + dutyAmount + shippingAmount;
  }, [order, dutyAmount, shippingAmount, subTotalAmount, taxAmount, taxesIncluded]);

  const shippingTitle = useMemo(() => {
    if (order?.shippingAddress?.territoryCode === CountryCode.Kr) {
      return "CJ 대한통운";
    }

    return getTrackingInfoCaa(order)?.company || "";
  }, [order]);

  return {
    totalQuantity,
    shippingAmount,
    taxAmount,
    dutyAmount,
    totalAmount,
    subTotalAmount,
    refundAmount,
    autoDiscountAmount,
    autoDiscountTitle,
    codeDiscountAmount,
    codeDiscountTitle,
    shippingTitle,
  };
}
